import { Component, EnvironmentInjector, HostBinding, OnDestroy, OnInit, Renderer2, NgZone } from '@angular/core';
import { GlobalSettingsService } from './core/globalSettings.service';
import { RouterInterceptorService } from './core/routerInterceptor.service';
import { PlatformDetectorService } from './core/platformDetector.service';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { QueryStringParams } from 'assets/consts';
import { EupHttpHandler } from './core/eupHttpHandler.service';
import { EupRoutesService } from './core/eupRoutes.service';
import { AppearanceService } from './core/appearance.service';
import { Appearance, FeatureToggle } from './shared/enums';
import { Observable, Subscription, of } from 'rxjs';
import { take, tap, catchError, filter, switchMap } from 'rxjs/operators';
import { DownloadNotificationService } from './shared/downloadNotification/downloadNotification.service';
import { FileInfo } from './shared/downloadNotification/fileInfo.model';
import { SettingsService } from '@shared/settings.service';
import { Utils } from '@shared/utils.service';
import { environment } from 'environments/environment';
import { AppConfigService } from './services/appConfig/appConfigService';
import { FeaturesToggleSettingsService } from './featuresToggleSettings/service/featuresToggleSettings.service';
import { SplunkRum } from '@splunk/otel-web';
import { AuthService } from '../app/services/authentication/auth.service';
import { SessionExpiredEventInfo } from './shared/generalInterfaces';
import { LogoutParameters } from './services/authentication/models/logout-parameters';
import { PlatformLoginService } from './services/platform-login/platform-login.service';
import { ShellContextService } from './services/shell-context/shell-context.service';
import { ShellCommunicationService } from './services/shell-communication/shell-communication.service';
import { Consts } from '@shared/consts';
import { FeatureToggleSettings } from './featuresToggleSettings/featuresToggleSettings.model';
import { PlatformCommunicationService } from './platform/services/platform-communication/platform-communication.service';
import { PendoService } from '@shared/pendo/pendo.service';
import { SpinnerService } from '@core/spinner/spinner.service';
import { TimberService } from '@logging/timber.service';
import { AuthAngularService } from '@itero/auth-angular';
import { AppTelemetryService } from './services/appTelemetry/apptelemetry.service';
import { initializeRemoteApplicationLoader } from '@itero/standard-launch-sdk';
import { UiFaceliftService } from '@shared/facelift-store/ui-facelift.service';

export class AppConfig {}

@Component({
	selector: 'body',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
	browserLang = this.translateService.getBrowserLang();
	browserCultureLang = this.translateService.getBrowserCultureLang();
	isScanner: boolean;
	isProduction: boolean;
	ftSubscription: Subscription;
	authSubscription: Subscription;
	isSpinnerActive = false;
	private files$: Observable<FileInfo[]>;
	private spinnerSubscription: Subscription;

	@HostBinding('lang') lang = 'en-US';
	@HostBinding('class.light-theme') lightThemeClass = false;

	@HostBinding('class.windows') windowsClass: boolean;
	@HostBinding('class.ie') ieClass: boolean;
	@HostBinding('class.edge') edgeClass: boolean;
	@HostBinding('class.is-tablet') isTabletClass: boolean;
	@HostBinding('class.iPad') iPadClass: boolean;
	@HostBinding('class.mac') macClass: boolean;

	constructor(
		private http: EupHttpHandler,
		private translateService: TranslateService,
		private globalSettingsService: GlobalSettingsService,
		private routerInterceptor: RouterInterceptorService,
		private platformDetector: PlatformDetectorService,
		private appearanceService: AppearanceService,
		private eupRoutesService: EupRoutesService,
		private settingsService: SettingsService,
		private downloadNotificationService: DownloadNotificationService,
		private utils: Utils,
		private appConfigService: AppConfigService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private authService: AuthService,
		private renderer: Renderer2,
		private shellContextService: ShellContextService,
		private shellCommunicationService: ShellCommunicationService,
		private platformCommunicationService: PlatformCommunicationService,
		private pendoService: PendoService,
		private spinnerService: SpinnerService,
		private timberService: TimberService,
		private authAngularService: AuthAngularService,
		private readonly appTelemetryService: AppTelemetryService,
		private readonly ngZone: NgZone,
		private readonly injector: EnvironmentInjector,
		private readonly uiFaceliftService: UiFaceliftService,
		private readonly platformLoginService: PlatformLoginService
	) {
		this.spinnerSubscription = this.spinnerService.onActiveChanged.subscribe((isActive: boolean) => {
			this.isSpinnerActive = isActive;
		});
	}

	ngOnDestroy(): void {
		this.shellCommunicationService.removeEventListeners(window);
		this.downloadNotificationService.clear();
		this.utils.clearObservablesSubscriptions(this);
		
		// Clean up auth subscription
		if (this.authSubscription) {
			this.authSubscription.unsubscribe();
		}
		
		// Clean up auth service as recommended by @itero/auth-angular README
		this.authAngularService.ngOnDestroy();
	}

	ngOnInit() {
		this.uiFaceliftService.initOrUpdateFaceliftAvailability();
		this.platformCommunicationService.subscribeToPlatformChannel();
		this.platformCommunicationService.subscribeToApplicationChannel();
		this.shellCommunicationService.subscribeToPlatformChannel();
		this.shellCommunicationService.subscribeToApplicationChannel();
		window.addEventListener('midc_event_onTokenExpired', this.handleTokenExpired.bind(this));

		// Initialize auth library
		this.initializeAuth();
		
		this.shellCommunicationService.initEventListeners(window);
		if (environment.production  && localStorage.getItem('timber_console_log') !== 'true') {
			console.log = () => {};
		}
		this.routerInterceptor.initialize();
		this.platformDetector.initialize();

		this.windowsClass = this.platformDetector.isWindows;
		this.ieClass = this.platformDetector.isIE;
		this.edgeClass = this.platformDetector.isEdge;
		this.isTabletClass = this.platformDetector.isTablet;
		this.iPadClass = this.platformDetector.isiPad;
		this.macClass = this.platformDetector.isMac;

		this.isScanner = this.utils.isScanner();
		this.translateService.setDefaultLang('en-US');
		if (!this.isSetLanguageFromUrl()) {
			this.setLanguage();
		}

		this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
			if (event.lang) {
				this.lang = event.lang;
			}
		});

		this.appearanceService.appearanceChanged$.subscribe((theme) => {
			this.lightThemeClass = theme === Appearance.Light;
		});

		this.appearanceService.responsiveChanged$.subscribe((isResponsive) => {
			if (isResponsive) {
				this.renderer.addClass(document.body, 'responsive');
			} else {
				this.renderer.removeClass(document.body, 'responsive');
			}
		});

		this.appTelemetryService.initialize();

		this.ftSubscription = this.featuresToggleSettingsService
			.getFeaturesToggleSettings()
			.pipe(
				tap((res: FeatureToggleSettings[]) => {
					const subscribeExportFilesOnlyOnce =
						res.find((f) => f.id === FeatureToggle.SubscribeExportFilesOnlyOnce)?.isActive === true;
					if (subscribeExportFilesOnlyOnce) {
						this.files$ = this.downloadNotificationService.getFiles();
					} else {
						this.files$ = this.downloadNotificationService.getObservable();
					}

				const splunkRumFeature = res.find((f) => f.id === FeatureToggle.SplunkRum)?.isActive === true;
				if (!splunkRumFeature && SplunkRum.inited) {
					try {
						SplunkRum.deinit();
					} catch (error) {
						this.timberService.error('Failed to deinitialize SplunkRum:', { module: 'AppComponent', error: error });
					}
				}
				if (splunkRumFeature && !SplunkRum.inited) {
					try {
						SplunkRum.init({
							beaconUrl: this.appConfigService.appSettings.signalFx.beaconUrl,
							rumAuth: this.appConfigService.appSettings.signalFx.rumAuth,
							app: this.appConfigService.appSettings.signalFx.app,
							environment: this.appConfigService.appSettings.signalFx.environment,
							instrumentations: null,
						});
					} catch (error) {
						this.timberService.error('Failed to initialize SplunkRum:', { module: 'AppComponent', error: error });
					}
				}
			}), catchError((error) => {
					this.timberService.error(`Error in feature toggle settings:`, {
						module: 'AppComponent',
						error: error,
					});
					return of(undefined);
				})).subscribe();

		const globalSettings = this.globalSettingsService.get();
		if (globalSettings?.contactId && globalSettings?.selectedCompanyId) {
			this.pendoService.initializePendo(globalSettings.contactId, globalSettings.selectedCompanyId);
		}

		this.shellContextService.isContextReady().pipe(
			filter((isReady) => isReady && this.appConfigService.appSettings?.remoteApplicationLoaderUrl?.length > 0),
			take(1),
			switchMap((isReady) =>
				initializeRemoteApplicationLoader(this.injector, {
					ralUrl: this.appConfigService.appSettings.remoteApplicationLoaderUrl,
					initOptions: {
						ngZone: this.ngZone,
					}
				})
			)).subscribe();
	}

	isSetLanguageFromUrl(): boolean {
		const language = this.utils.getUrlParameter(QueryStringParams.LANGUAGE);
		if (language) {
			this.setLang(this.utils.getValidLanguage(language));
			return true;
		}
		return false;
	}

	private handleTokenExpired(event: any): void {
		const sessionInfo = localStorage.getItem(Consts.Storage.SessionInfo);
		if (sessionInfo) {
			const eventDetail = (<CustomEvent>event).detail as SessionExpiredEventInfo;
			if (eventDetail.sessionId) {
				const logoutParams = this.getLogoutParams(eventDetail);
				const returnUrl = logoutParams.returnUrl;

				if (returnUrl && this.shouldRelogin()) {
					this.authService.relogin(returnUrl).subscribe();
				} else {
					this.authService.logout(eventDetail.sessionId, logoutParams).subscribe();
				}
			}
		}
	}

	private shouldRelogin(): boolean {
		const useIteroPlatformLogin = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.UseIteroPlatformLogin, false);
		return useIteroPlatformLogin && this.platformLoginService.iteroPlatformLoginUsed();
	}

	private initializeAuth(): void {
		this.authService.restoreAuthFromStorage();
	}

	private setLanguage(): void {
		const globalSettings = this.globalSettingsService.get();
		// try to set the language according to the selected language of the logged in user
		if (globalSettings && globalSettings.selectedLanguage) {
			this.setLang(globalSettings.selectedLanguage.code);
			this.eupRoutesService.doAfterInit(() => of({}), true).subscribe();
		} else {
			// in order to detect browser language for ie & edge needs http request
			if (this.platformDetector.isIE || this.platformDetector.isEdge) {
				this.setLang('en-US');
				const getLangCode = () => this.http.get(this.eupRoutesService.login.browserLanguageCode, undefined, false, false);
				this.eupRoutesService.doAfterInit(getLangCode, true).subscribe(
					(res: any) => {
						this.setLang(res.detectedCulture);
					},
					() => {
						this.setLang('en-US');
					}
				);
			} else {
				this.settingsService
					.getLocalizationSettings()
					.pipe(take(1))
					.subscribe((res: any) => {
						const supportedLangs = res.languages.map((i) => i.code);
						if (this.browserCultureLang && supportedLangs.indexOf(this.browserCultureLang) > -1) {
							this.setLang(this.browserCultureLang);
						} else {
							if (this.browserLang && supportedLangs.filter((sl) => sl.substring(0, 2) === this.browserLang)[0]) {
								this.setLang(supportedLangs.filter((sl) => sl.substring(0, 2) === this.browserLang)[0]);
							}
						}
					});
			}
		}
	}

	private setLang(lang: string): void {
		this.lang = lang;
		if (!lang) {
			const globalSettings = this.globalSettingsService.get();
			if (globalSettings && globalSettings.selectedLanguage) {
				this.lang = globalSettings.selectedLanguage.code;
			}
		}

		this.translateService.use(this.lang);
		this.shellContextService.updateContext((ctx) => {
			ctx.UI.language = this.lang;
		});
	}
	private getLogoutParams(event: SessionExpiredEventInfo): LogoutParameters {
		const logoutParams: LogoutParameters = {};
		if (event.payload?.returnUrl) {
			logoutParams.returnUrl = event.payload.returnUrl;
		}
		return logoutParams;
	}
}

export class BrowserInfo {
	detectedCulture: string;
}
